import { trace, type Tracer } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';

import type { MetricsSpanProcessor } from './metrics-span.processor';

// Well-known OpenTelemetry semantic convention attribute keys. Used as plain
// strings to avoid pulling in the sometimes-unstable `incubating` subpath
// export of `@opentelemetry/semantic-conventions`.
const ATTR_SERVICE_NAME = 'service.name';
const ATTR_SERVICE_VERSION = 'service.version';
const ATTR_DEPLOYMENT_ENVIRONMENT_NAME = 'deployment.environment.name';

const TRACER_NAME = 'spri-client.metrics';

let provider: WebTracerProvider | null = null;
let tracer: Tracer | null = null;
let processor: MetricsSpanProcessor | null = null;

export interface InitializeOtelWebOptions {
  serviceName: string;
  serviceVersion?: string;
  environmentName: string;
  processor: MetricsSpanProcessor;
}

/**
 * Bootstraps the browser-side OpenTelemetry tracer once per page lifetime.
 *
 * Idempotent: a second call replaces the existing provider so hot-reload in
 * dev doesn't double-register processors. On every call the previous
 * processor (if any) is shut down first to drain its buffer.
 */
export async function initializeOtelWeb(options: InitializeOtelWebOptions): Promise<Tracer> {
  if (processor && processor !== options.processor) {
    await processor.shutdown();
  }
  processor = options.processor;

  provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: options.serviceName,
      [ATTR_SERVICE_VERSION]: options.serviceVersion ?? 'unknown',
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: options.environmentName,
    }),
    spanProcessors: [options.processor],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  tracer = trace.getTracer(TRACER_NAME);
  return tracer;
}

export function getMetricsTracer(): Tracer {
  return tracer ?? trace.getTracer(TRACER_NAME);
}

/**
 * Test helper: resets the global tracer state. Not exported from `index.ts`
 * because production code should not call it.
 */
// eslint-disable-next-line no-underscore-dangle -- test-only helper convention
export async function _resetOtelForTests(): Promise<void> {
  if (processor) {
    await processor.shutdown();
  }
  if (provider) {
    await provider.shutdown();
  }
  provider = null;
  tracer = null;
  processor = null;
  trace.disable();
}
